import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ProviderAccount } from '@biz-action/system-actions/account-overview.actions';
import {
	ChangePasswordFail,
	ChangePasswordSuccess,
	ValidateAuthTokenSuccess,
} from '@biz-action/system-actions/auth-token.actions';
import { SetCurrentUser, SetCurrentUserFailed } from '@biz-action/system-actions/current-user-system.actions';
import { ValidationError } from '@biz-action/system-actions/general.actions';
import { socialLinkAccount } from '@biz-action/system-actions/social-account.action';
import { authenticationAccount } from '@biz-action/user-actions/account-actions';
import { authentication } from '@biz-action/user-actions/auth-login.actions';
import { LinkConfirm, socialLink } from '@biz-action/user-actions/link-an-account.actions';

import { L7Response } from '@biz-models/network/l7-response.model';
import { socialLinkReq, subdomainLinkReq } from '@biz-models/network/login-authenticate-network.model';
import { UserProfileNetworkModel } from '@biz-models/network/user-profile-network.model';
import { CurrentUserModel } from '@biz-models/shared/current-user.model';
import { SnackbarErrorComponent } from '@biz-ui/shared-presenters/custom-snackbar/snackbar-error/snackbar-error.component';
import { SnackbarSuccessComponent } from '@biz-ui/shared-presenters/custom-snackbar/snackbar-success/snackbar-success.component';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { getRouterSelectors as getSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ForgetPasswordService } from 'src/app/services/forget-password.service';
import { LoginService } from 'src/app/services/login.service';
import { RegisterService } from 'src/app/services/register.service';
import { SelectedSubdomain } from 'src/app/settings/linked-accounts/linking-confirm-dialog/selectors';
import { AuthApi } from 'src/controls/api/auth/auth.api';
@Injectable({
	providedIn: 'root',
})
export class CurrentUserEffects {
	constructor(
		private actions$: Actions,
		private store: Store,
		private authApi: AuthApi,
		private passwordService: ForgetPasswordService,
		private loginService: LoginService,
		private _snackBar: MatSnackBar,
		private registerService: RegisterService,
		private router: Router,
	) {}

	retrieveCurrentUserProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ValidateAuthTokenSuccess),
			switchMap(() =>
				this.authApi.getUserProfile$().pipe(
					map(this._getUserDataFromResponse),
					map((userData) => ({
						userData,
						selectedLanguage: this._getLocalLanguage(),
					})),
					map(this._mapResponseDataToCurrentUserModel),
					map((userData) => SetCurrentUser(userData)),
					catchError((err) => of(SetCurrentUserFailed(err.message))),
				),
			),
		),
	);

	changePassword$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authenticationAccount.changepassword),
			map((value) => ({
				password: value.payload.current,
				new_password_confirmation: value.payload.confirm,
				new_password: value.payload.new,
			})),
			switchMap((payload) =>
				this.passwordService.changePassword(payload).pipe(
					map((resp) => authenticationAccount.changepasswordsuccess(resp.data)),
					catchError((err) => of(authenticationAccount.changepasswordfail(err.message))),
				),
			),
		),
	);

	socialLoginAuth$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.authstatechanged),
			concatLatestFrom(() => this.store.select(getSelectors().selectUrl)),
			filter(([, url]) => url.includes('social-login')),
			map(([data]) => ({
				token: data.payload.provider == 'GOOGLE' ? data.payload.idToken : data.payload.authToken,
				provider: data.payload.provider.toLocaleLowerCase() as socialLinkReq['provider'],
			})),
			map((payload) => socialLink.sociallink(payload)),
		),
	);

	socialLinkAccount$ = createEffect(() =>
		this.actions$.pipe(
			ofType(socialLink.sociallink),
			switchMap((action) =>
				this.loginService.socialLink(action.payload).pipe(
					map((resp) => {
						return socialLinkAccount.sociallinksuccess(resp.data);
					}),
					catchError((err) => of(socialLinkAccount.sociallinkfail(err.message))),
				),
			),
		),
	);

	providerAccount$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ProviderAccount.getproviderlist),
			switchMap(() =>
				this.loginService.listLinkAccount().pipe(
					map((resp) => {
						return ProviderAccount.getproviderlistsuccess(resp.data);
					}),
					catchError((err) => of(ProviderAccount.getproviderlistfail(err.message))),
				),
			),
		),
	);

	prepareLinkAccount$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(LinkConfirm),
			concatLatestFrom(() => this.store.select(SelectedSubdomain)),
			map(
				([, payload]) =>
					({
						link_subdomain: payload.subdomain,
						link_token: payload.token,
					} as subdomainLinkReq),
			),
			map((payload) => authenticationAccount.linkaccount(payload)),
		);
	});

	linkAccountSubdomain = createEffect(() =>
		this.actions$.pipe(
			ofType(authenticationAccount.linkaccount),
			switchMap((action) =>
				this.loginService.linkSubdoamin(action.payload).pipe(
					map((resp) => {
						return authenticationAccount.linkaccountsuccess(resp.data);
					}),
					catchError((err) => of(ProviderAccount.getproviderlistfail(err.message))),
				),
			),
		),
	);

	unlinkAccountSubdomain$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authenticationAccount.unlinkaccount),
			switchMap((action) =>
				this.loginService.unlinkSubdomain(action.payload).pipe(
					map((resp) => {
						return authenticationAccount.unlinkaccountsuccess(resp.data);
					}),
					catchError((err) => of(ProviderAccount.getproviderlistfail(err.message))),
				),
			),
		),
	);

	socialUnlinkAccount$ = createEffect(() =>
		this.actions$.pipe(
			ofType(socialLink.socialunlink),
			switchMap((action) =>
				this.loginService.socialUnlink(action.payload.provider_id).pipe(
					map((resp) => socialLinkAccount.socialunlinksuccess(resp.data)),
					catchError((err) => of(socialLinkAccount.socialunlinkfail(err))),
				),
			),
		),
	);

	listSubdomain$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				authenticationAccount.loadsubdomainlist,
				authenticationAccount.linkaccountsuccess,
				authenticationAccount.unlinkaccountsuccess,
			),
			switchMap(() =>
				this.registerService.listSubdomain().pipe(
					map((resp) => {
						return authenticationAccount.loadsubdomainlistsuccess(resp.data);
					}),
					catchError((err) => of(authenticationAccount.loadsubdomainlistfail(err))),
				),
			),
		),
	);

	resetPassword = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.resetpassword),
			switchMap((data) =>
				this.passwordService.resetPassword(data.payload).pipe(
					map((resp) => {
						return authentication.resetpasswordsuccess(resp.data);
					}),
					catchError((err) => of(authenticationAccount.loadsubdomainlistfail(err))),
				),
			),
		),
	);

	ChangePasswordReset$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authentication.resetpasswordsuccess),
				map(() => {
					this._snackBar.openFromComponent(SnackbarSuccessComponent, {
						data: 'Password reset successful! Return to login page now',
						panelClass: 'bg-success',
					});
				}),
				map(() => {
					this.router.navigate(['/login']);
				}),
			),
		{ dispatch: false },
	);

	ChangePasswordMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authenticationAccount.changepasswordsuccess),
				map(() =>
					this._snackBar.openFromComponent(SnackbarSuccessComponent, {
						data: 'Successfully password changed',
						panelClass: 'bg-success',
					}),
				),
			),
		{ dispatch: false },
	);

	LinkMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(socialLinkAccount.sociallinksuccess, authenticationAccount.linkaccountsuccess),
				map(() =>
					this._snackBar.openFromComponent(SnackbarSuccessComponent, {
						data: 'Success Link Account',
						panelClass: 'bg-success',
					}),
				),
			),
		{ dispatch: false },
	);

	UnlinkMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(socialLinkAccount.socialunlinksuccess, authenticationAccount.unlinkaccountsuccess),
				map(() =>
					this._snackBar.openFromComponent(SnackbarSuccessComponent, {
						data: 'Success Unlink Account',
						panelClass: 'bg-success',
						duration: 3000,
					}),
				),
			),
		{ dispatch: false },
	);

	ErrorMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					ProviderAccount.getproviderlistfail,
					authenticationAccount.loadsubdomainlistfail,
					socialLinkAccount.socialunlinkfail,
				),
				map((v) =>
					this._snackBar.openFromComponent(SnackbarErrorComponent, {
						data: v.payload,
						panelClass: 'bg-danger',
					}),
				),
			),
		{ dispatch: false },
	);

	validationError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(ValidationError),
				map((action) => action.payload.errors),
				map((errors) => {
					let ul = document.createElement('ul');
					ul.className = 'mb-0';
					Object.values(errors).map((err) => {
						let li = document.createElement('li');
						li.innerHTML = err[0];
						ul.appendChild(li);
					});
					return ul.outerHTML;
				}),
				tap((message) => {
					this._snackBar.openFromComponent(SnackbarErrorComponent, {
						data: message,
						panelClass: 'bg-danger',
					});
				}),
			),
		{ dispatch: false },
	);

	private _getUserDataFromResponse(res: L7Response<UserProfileNetworkModel>) {
		return res.data;
	}

	private _mapResponseDataToCurrentUserModel(value: {
		userData: UserProfileNetworkModel;
		selectedLanguage?: string;
	}): CurrentUserModel {
		return {
			...value.userData,
			// TODO: hard-coded granted to true, assume only granted permissions are returned
			permissions: value.userData.permissions.map((p) => ({
				key: p,
				granted: true,
			})),
			branch: {
				// ...value.userData.branch,
				id: value.userData.branch_id,
			},
			linked_to_facebook: !!value.userData.linked_to_facebook,
			selectedLanguage: value.selectedLanguage ?? value.userData.locale,
		};
	}

	private _getLocalLanguage(): string | undefined {
		return localStorage.getItem('selected_lang');
	}
}
